import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getSteps,
    hasExistingCCDetails,
    clientRequiresCreditCard,
    getIsDigitalWalkIn,
} from 'booking/redux/selectors';
import { BOOKING_STEPS, BookingStep } from 'booking/redux/types';
import Progress from 'core/components/Progress';
import { LocationWithCoordinates } from 'lib/types';
import * as styles from './BookingProgress.module.scss';
import { setSteps } from '../../redux/actions';
import useBookingContext from '../../hooks/useBookingContext';

type LocationWithBookingSettings = Pick<
    LocationWithCoordinates,
    'bookingSettings' | '_id'
>;

function createValues(
    steps: ReadonlyArray<BookingStep>,
): Partial<Record<BookingStep, number>> {
    return steps.reduce(
        (result, step) => ({
            ...result,
            [step]: ((steps.indexOf(step) + 1) / steps.length) * 100,
        }),
        {},
    );
}

function selectedLocationRequiresCreditCard(
    location?: LocationWithBookingSettings,
): boolean {
    if (!location) {
        return false;
    }

    return location?.bookingSettings?.requiresCreditCard || false;
}

function BookingProgress() {
    const { step, location } = useBookingContext();
    const steps = useSelector(getSteps);
    const hasExistingDetails = useSelector(hasExistingCCDetails);
    const clientReqiresCreditCard = useSelector(clientRequiresCreditCard);
    const [locationRequiresCreditCard, setLocationRequiresCreditCard] =
        React.useState(() => selectedLocationRequiresCreditCard(location));
    const isDigitalWalkIn = useSelector(getIsDigitalWalkIn);

    // monitor selected location
    React.useEffect(() => {
        let handle: number | undefined = requestAnimationFrame(() => {
            handle = requestAnimationFrame(() => {
                handle = undefined;
                setLocationRequiresCreditCard(
                    selectedLocationRequiresCreditCard(location),
                );
            });
        });

        return () => {
            if (handle !== undefined) {
                cancelAnimationFrame(handle);
            }
        };
    }, [location]);

    // update steps as necessary
    const dispatch = useDispatch();
    React.useEffect(() => {
        let newSteps = BOOKING_STEPS.slice(0);

        if (
            isDigitalWalkIn ||
            ((!locationRequiresCreditCard || hasExistingDetails) &&
                !clientReqiresCreditCard)
        ) {
            newSteps = BOOKING_STEPS.filter(
                (bookingStep) => bookingStep !== 'creditCard',
            );
        }

        dispatch(setSteps(newSteps));
    }, [
        locationRequiresCreditCard,
        hasExistingDetails,
        dispatch,
        clientReqiresCreditCard,
        isDigitalWalkIn,
    ]);

    const value = React.useMemo(() => {
        const values = createValues(steps);

        return values[step] || 100 / steps.length;
    }, [step, steps]);

    return <Progress className={styles.root} value={value} />;
}

export default BookingProgress;
